import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { apiService } from '../services/api';

const GarageContext = createContext();

export const useGarage = () => {
  const context = useContext(GarageContext);
  if (!context) {
    throw new Error('useGarage must be used within a GarageProvider');
  }
  return context;
};

export const GarageProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [garages, setGarages] = useState([]);
  const [selectedGarage, setSelectedGarage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetched, setFetched] = useState(false);

  const fetchGarages = async () => {
    if (fetched || loading) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getMyGarages();
      setGarages(response.data || []);
      
      // Select first garage by default
      if (response.data && response.data.length > 0) {
        setSelectedGarage(response.data[0]);
      }
      
      setFetched(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load garages');
      console.error('Failed to fetch garages:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshGarages = () => {
    setFetched(false);
    fetchGarages();
  };

  useEffect(() => {
    if (user?.role === 'garage_admin' && isAuthenticated && !fetched) {
      fetchGarages();
    }
    
    // Reset garage context when user logs out or changes
    if (!isAuthenticated || !user) {
      setGarages([]);
      setSelectedGarage(null);
      setFetched(false);
      setError(null);
    }
  }, [user, isAuthenticated, fetched]);

  const value = {
    garages,
    selectedGarage,
    setSelectedGarage,
    loading,
    error,
    refreshGarages,
    fetched
  };

  return (
    <GarageContext.Provider value={value}>
      {children}
    </GarageContext.Provider>
  );
};